// src/components/popups/TopUpDrawer.js

import React, { useMemo, useState } from 'react';
import './TopUpDrawer.css';
import { useTranslation } from 'react-i18next';

const PRESET_AMOUNTS = [0.1, 0.5, 2.5, 12.5, 62.5];

function TopUpDrawer({ onClose, onTopUp, minAmount = 0.1 }) {
  const { t } = useTranslation();

  const defaultPresetIndex = PRESET_AMOUNTS.findIndex(
    (value) => Number(value) >= Number(minAmount)
  );

  const initialPresetIndex =
    defaultPresetIndex >= 0 ? defaultPresetIndex : null;

  const initialAmount =
    initialPresetIndex !== null
      ? String(PRESET_AMOUNTS[initialPresetIndex])
      : String(minAmount);

  const [amount, setAmount] = useState(initialAmount);
  const [selectedPreset, setSelectedPreset] = useState(initialPresetIndex);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');

  // Allow comma input on iOS, but normalize it to a dot for JavaScript.
  const normalizedAmount = useMemo(() => {
    if (!amount) return '';

    return String(amount).replace(',', '.');
  }, [amount]);

  const numeric = useMemo(() => {
    return Number(normalizedAmount);
  }, [normalizedAmount]);

  const amountInvalid =
    !amount ||
    !Number.isFinite(numeric) ||
    numeric <= 0;

  const amountTooLow =
    Number.isFinite(numeric) &&
    numeric > 0 &&
    numeric < Number(minAmount);

  const disableSubmit =
    isSubmitting ||
    amountInvalid ||
    amountTooLow;

  const handlePresetClick = (value, index) => {
    if (isSubmitting) return;

    setAmount(String(value));
    setSelectedPreset(index);
    setLocalError('');
  };

  const handleAmountChange = (e) => {
    let value = e.target.value || '';

    // Keep only numbers, comma and dot.
    value = value.replace(/[^0-9.,]/g, '');

    const firstComma = value.indexOf(',');
    const firstDot = value.indexOf('.');

    const firstSeparator = (() => {
      if (firstComma === -1) return firstDot;
      if (firstDot === -1) return firstComma;

      return Math.min(firstComma, firstDot);
    })();

    // Keep only one decimal separator.
    if (firstSeparator !== -1) {
      const beforeSeparator = value.slice(0, firstSeparator + 1);

      const afterSeparator = value
        .slice(firstSeparator + 1)
        .replace(/[.,]/g, '');

      value = beforeSeparator + afterSeparator;
    }

    setAmount(value);
    setSelectedPreset(null);
    setLocalError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (amountInvalid) {
      setLocalError(t('topup.errors.invalid_amount'));
      return;
    }

    if (numeric < Number(minAmount)) {
      setLocalError(
        t('topup.errors.min', {
          min: minAmount,
        })
      );
      return;
    }

    setLocalError('');
    setIsSubmitting(true);

    try {
      // MyWallet closes the drawer after a successful payment.
      await onTopUp(numeric);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const stopPropagation = (e) => {
    e.stopPropagation();
  };

  return (
    <div
      className="topup-drawer-backdrop"
      onClick={handleBackdropClick}
    >
      <div
        className="topup-drawer-content"
        onClick={stopPropagation}
        onMouseDown={stopPropagation}
        onTouchStart={stopPropagation}
      >
        <div className="topup-header">
          <span>{t('topup.title')}</span>

          <button
            className="topup-close"
            onClick={onClose}
            disabled={isSubmitting}
            type="button"
            aria-label="Close top up drawer"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <input
            type="text"
            className="topup-input"
            placeholder={t('topup.amount_placeholder', {
              min: minAmount,
            })}
            value={amount}
            inputMode="decimal"
            autoComplete="off"
            onChange={handleAmountChange}
            disabled={isSubmitting}
          />

          <div className="topup-presets">
            {PRESET_AMOUNTS.map((value, index) => {
              const presetBelowMinimum =
                Number(value) < Number(minAmount);

              return (
                <button
                  key={value}
                  type="button"
                  className={`topup-preset-btn ${
                    selectedPreset === index ? 'active' : ''
                  }`}
                  onClick={() => handlePresetClick(value, index)}
                  disabled={isSubmitting || presetBelowMinimum}
                >
                  {value} Pi
                </button>
              );
            })}
          </div>

          {amountTooLow && !amountInvalid && (
            <div className="topup-inline-error">
              {t('topup.errors.min', {
                min: minAmount,
              })}
            </div>
          )}

          {localError && (
            <div className="topup-inline-error">
              {localError}
            </div>
          )}

          <button
            className="topup-send-btn"
            type="submit"
            disabled={disableSubmit}
          >
            {isSubmitting
              ? t('topup.starting')
              : t('topup.submit')}
          </button>
        </form>
      </div>
    </div>
  );
}

export default TopUpDrawer;
